import React from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onBrowseWebinars, onBecomeInstructor }) => {
  return (
    <section className="relative bg-gradient-to-br from-green-50 via-emerald-50 to-white text-gray-900 overflow-hidden min-h-[90vh] flex items-center">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating geometric shapes */}
        <motion.div
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-green-200/40 to-emerald-200/40 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, 30, 0],
            x: [0, -20, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-emerald-200/30 to-green-200/30 rounded-full blur-3xl"
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(to right, #10b981 1px, transparent 1px),
                             linear-gradient(to bottom, #10b981 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content - Text Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6 md:space-y-8 text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center"
              >
                <Icon name="Shield" size={12} className="text-white" />
              </motion.div>
              <span className="text-sm font-semibold text-green-800">Trusted by 500+ Companies</span>
            </motion.div>

            {/* Main Heading */}
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
              >
                Master Workplace
                <br />
                <motion.span
                  className="relative inline-block"
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    backgroundImage: 'linear-gradient(90deg, #10b981, #059669, #10b981)',
                    backgroundSize: '200% auto',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  Compliance Excellence
                </motion.span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed"
              >
                Transform complex regulations into confident actions with our expert-led training programs. 
                Stay ahead of OSHA, HIPAA, ADA, and HR compliance requirements with actionable insights.
              </motion.p>
            </div>

            {/* Interactive Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 md:gap-6 py-4"
            >
              {[
                { number: '98%', label: 'Satisfaction Rate', icon: 'Star' },
                { number: '24/7', label: 'Expert Support', icon: 'Headphones' },
                { number: '1M+', label: 'Learning Hours', icon: 'Clock' }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.2)'
                  }}
                  className="flex-1 min-w-[120px] bg-white rounded-xl p-4 border border-green-100 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                      <Icon name={stat.icon} size={18} className="text-green-600" />
                    </div>
                    <motion.div
                      className="text-2xl font-bold text-green-700"
                      animate={{
                        textShadow: [
                          '0 0 0px rgba(16, 185, 129, 0)',
                          '0 0 10px rgba(16, 185, 129, 0.3)',
                          '0 0 0px rgba(16, 185, 129, 0)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {stat.number}
                    </motion.div>
                  </div>
                  <div className="text-sm font-medium text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto"
              >
                <Button
                  variant="default"
                  size="lg"
                  onClick={onBrowseWebinars}
                  className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl shadow-lg hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 transform"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Explore Training Programs
                </Button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto"
              >
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onBecomeInstructor}
                  className="w-full sm:w-auto border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md"
                  iconName="PlayCircle"
                  iconPosition="left"
                >
                  Watch Demo Session
                </Button>
              </motion.div>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="pt-6"
            >
              <div className="text-sm text-gray-500 mb-3">Trusted by industry leaders:</div>
              <div className="flex flex-wrap items-center gap-4">
                {['SHRM', 'HRCI', 'ISO 9001', 'NASP'].map((badge, index) => (
                  <motion.div
                    key={badge}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 + index * 0.1 }}
                    whileHover={{ y: -3 }}
                    className="px-3 py-2 bg-white rounded-lg border border-green-100 shadow-sm"
                  >
                    <span className="text-sm font-semibold text-green-700">{badge}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Visual Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Main Card */}
            <div className="relative z-10">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative bg-white rounded-2xl overflow-hidden shadow-2xl shadow-green-900/10 border border-green-100"
              >
                {/* Card Header */}
                <div className="relative h-48 sm:h-56 md:h-64 bg-gradient-to-r from-green-500 to-emerald-600">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="relative"
                    >
                      <Icon name="BookOpen" size={80} className="text-white/90" />
                    </motion.div>
                  </div>
                  
                  {/* Floating badges */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                        <Icon name="Video" size={14} className="text-white" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-green-800">Live Now</div>
                        <div className="text-xs text-gray-600">Join 500+ attendees</div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Featured Training Session</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Advanced OSHA Compliance: 2025 Regulatory Updates
                  </p>
                  
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center">
                        <Icon name="User" size={16} className="text-green-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold">Dr. Sarah Johnson</div>
                        <div className="text-xs text-gray-500">Senior Compliance Expert</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-600">Today</div>
                      <div className="text-xs text-gray-500">2:00 PM EST</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Seats Filling Fast</span>
                      <span>85%</span>
                    </div>
                    <div className="h-2 bg-green-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '85%' }}
                        transition={{ duration: 1.5, delay: 0.8 }}
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-600"
                      />
                    </div>
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full"
                  >
                    <Button
                      onClick={onBrowseWebinars}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 rounded-lg"
                    >
                      <Icon name="Calendar" size={16} className="mr-2" />
                      Reserve Your Seat
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 3, repeat: Infinity }
              }}
              className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-r from-green-200/20 to-emerald-200/20 rounded-full blur-xl"
            />

            {/* Floating Card 1 */}
            <motion.div
              initial={{ x: 50, y: 50, opacity: 0 }}
              animate={{ x: 0, y: 0, opacity: 1 }}
              transition={{ delay: 0.6, type: "spring" }}
              whileHover={{ scale: 1.05, rotate: 2 }}
              className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl border border-emerald-100 max-w-[180px]"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Award" size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">Certification</div>
                  <div className="text-xs text-gray-600">CPE Credits Available</div>
                </div>
              </div>
            </motion.div>

            {/* Floating Card 2 */}
            <motion.div
              initial={{ x: -50, y: -50, opacity: 0 }}
              animate={{ x: 0, y: 0, opacity: 1 }}
              transition={{ delay: 0.8, type: "spring" }}
              whileHover={{ scale: 1.05, rotate: -2 }}
              className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-xl border border-green-100 max-w-[180px]"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Download" size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">Resources</div>
                  <div className="text-xs text-gray-600">Download Materials</div>
                </div>
              </div>
            </motion.div>

            {/* Animated Dots */}
            <motion.div
              animate={{
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute -bottom-8 -left-8 w-24 h-24 opacity-10"
            >
              <div className="absolute inset-0 rounded-full border-2 border-green-400" />
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-2 border-green-300" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;